import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { omit } from 'ramda';
import { cloneDeep } from 'lodash';
import moment from 'moment';

import { Formik, Form } from 'formik';
import { Grid, Box, Typography, Checkbox, Tooltip, IconButton, FormControlLabel, FormHelperText } from '@material-ui/core';
import Card from '../../../components/Card';
import CardFooter from '../../../components/CardFooter';
import Input from '../../../components/Input';
import Loader from '../../../components/Loader';
import Button from '../../../components/Button';
import MTable from '../../../components/MTable';
import TableMobile from '../../../components/TableMobile';
import Modal from '../../../components/Modal';
import OTP from '../../../components/OTP';
import PasswordUpdate from '../../../components/PasswordUpdate';

import { FinancialResumeAllEstablishments, UsageResume } from '../../../components/ExportPDF/templates';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

import { loadProfile, upsertUser, updateUserPassword, loadMfa, enableMfa, disableMfa, enableMfaConfirm, disableMfaConfirm } from '../store/thunk';
import { clearProfile } from '../store/actions';
import { loadFilters } from 'components/Filter/store/thunk';
import { loadAllEstablishmentsFinancialAndUsageResumeList, loadAllEstablishmentsFinancialAndUsageResumeReport } from '../../CashFlow/store/thunk';

import { getPasswordStrength, getPasswordRules } from '../../../components/PasswordUpdate/resources';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import { createPDF } from 'helpers/common';

import { validations } from './validations';

const allEstablishmentsReportHeaders = [
  { title: 'Relatório', field: 'description' },
  { title: 'Data/Hora', field: 'createdAt' }
];

export default function Profile({ formikProps }) {
  const dispatch = useDispatch();

  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [isMfaCodeModalOpen, setIsMfaCodeModalOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaCodeError, setMfaCodeError] = useState('');

  const { width } = useWindowDimensions();
  const isMobile = width < 960;

  const { path } = useRouteMatch();
  const isInitialRegistration = path.split('/').includes('cadastro-inicial');

  const [isPasswordUpdateOpen, setIsPasswordUpdateOpen] = useState(false);
  const [isPasswordUpdateLoading, setIsPasswordUpdateLoading] = useState(false);

  const {
    userId,
    isAdminUser,
    submit,
    mfa,
    isLoading
  } = useSelector(state => state.profile);

  const {
    isLoading: isCashFlowLoading,
    allEstablishmentsReportList
  } = useSelector(state => state.cashFlow);

  const { allEstablishments } = useSelector(state => state.login);

  const servicesCategories = useSelector(state => state.filters)
    ?.selects
    ?.find(filter => filter?.id == 'serviceCategory')
    ?.options;

  const isUserAdmin = +isAdminUser == 1;

  const initialValues = {
    userId,
    ...submit
  }

  useEffect(() => {
    if(userId) {
      dispatch(loadFilters(userId));
    }

    if(userId) {
      dispatch(loadProfile(userId));
    } else {
      dispatch(clearProfile());
    }
  }, [userId]);

  useEffect(() => {
    if(!isInitialRegistration) {
      dispatch(loadMfa(userId));
      dispatch(loadAllEstablishmentsFinancialAndUsageResumeList(userId));
    }
  }, []);

  const handleGenerateReport = reportId => {
    dispatch(loadAllEstablishmentsFinancialAndUsageResumeReport(userId, reportId)).then(data => {
      const { description, report, reportTypeId } = data;

      if(+reportTypeId == 1) {
        const formmatedData = Object.entries(report).map(([establishmentId, data]) => {
          return {
            ...cloneDeep(data),
            establishmentId
          }
        });

        let component = pdf(
          <FinancialResumeAllEstablishments
            exportData={formmatedData}
            period={description}
            servicesCategories={servicesCategories}
            allEstablishments={allEstablishments}
          />
        );

        return createPDF(component);
      }

      if(+reportTypeId == 2) {
        const allEstablishmentsData = Object
          .keys(report)
          .filter(key => report[key]?.data?.length > 0)
          .map(key => report[key]?.data)
          .flat();

        let component = pdf(
          <UsageResume
            exportData={allEstablishmentsData}
            establishmentName='Todos os estabelecimentos'
            fileName={description}
            dateRange={['', '']}
          />
        );

        return createPDF(component);
      }
    });
  }

  const handleMfa = () => {
    setMfaCodeError('');

    if(!mfaCode) {
      return setMfaCodeError('Insira um código de verificação.');
    }

    if(mfaCode.length < 6) {
      return setMfaCodeError('O código de verificação deve ter 6 dígitos.');
    }

    if(+mfa?.email2faEnabled == 0) {
      dispatch(enableMfaConfirm(userId, mfaCode)).then(() => {
        setMfaCode('');
        setIsMfaCodeModalOpen(false);
      });
    }

    if(+mfa?.email2faEnabled == 1) {
      dispatch(disableMfaConfirm(userId, mfaCode)).then(() => {
        setMfaCode('');
        setIsMfaCodeModalOpen(false);
      });
    }
  }

  const handleCheckboxChange = (e, setFieldValue) => {
    const { name, checked } = e.target;
    setFieldValue(name, checked);
    formikProps.setFieldValue(name, checked);
  }

  const handleProfileSave = (values, actions) => {
    const { resetForm } = actions;
    const params = omit(['termsOfUse', 'emailConfirm', 'isInitialRegistration', 'isProfile', 'isProduction'], values);

    if(!params?.password) {
      delete params['password'];
      delete params['passwordConfirm'];
    }

    dispatch(upsertUser(params)).then(() => {
      resetForm();
    });
  }

  return(
    <>
      {isMfaModalOpen && (
        <Modal
          id='mfa-modal'
          maxWidth='sm'
          title='Dupla Autenticação'
          open={isMfaModalOpen}
          onClose={() => setIsMfaModalOpen(false)}
        >
          <Loader isLoading={isLoading}>
            <Grid container>
              <Grid item xs={12}>
                <Typography>
                  {+mfa?.email2faEnabled == 1 ? 'Deseja desativar a dupla autenticação?' : 'Deseja ativar a dupla autenticação?'}
                </Typography>
              </Grid>
              <Grid container xs={12} justify='flex-end' style={{ gap: 10 }}>
                <Button
                  type='button'
                  color='error'
                  onClick={() => setIsMfaModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type='button'
                  color='success'
                  onClick={() => {
                    if(+mfa?.email2faEnabled == 0) {
                      dispatch(enableMfa(userId)).then(() => {
                        setIsMfaCodeModalOpen(true);
                        setIsMfaModalOpen(false);
                      });
                    }

                    if(+mfa?.email2faEnabled == 1) {
                      dispatch(disableMfa(userId)).then(() => {
                        setIsMfaCodeModalOpen(true);
                        setIsMfaModalOpen(false);
                      });
                    }
                  }}
                >
                  Confirmar
                </Button>
              </Grid>
            </Grid>
          </Loader>
        </Modal>
      )}
      {isMfaCodeModalOpen && (
        <Modal
          id='mfa-code-modal'
          maxWidth='sm'
          title='Verificação'
          open={isMfaCodeModalOpen}
          onClose={() => {
            setMfaCode('');
            setIsMfaCodeModalOpen(false);
          }}
        >
          <Loader isLoading={isLoading}>
            <Grid container>
              <Grid item xs={12}>
                <Typography>
                  Insira o código de verificação enviado para o seu email para {+mfa?.email2faEnabled == 1 ? 'desativar' : 'ativar'} a dupla autenticação.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <OTP
                  value={mfaCode}
                  error={!!mfaCodeError}
                  helperText={mfaCodeError}
                  onChange={code => setMfaCode(code)}
                />
              </Grid>
              <Grid container xs={12} justify='flex-end' style={{ gap: 10, marginTop: 10 }}>
                <Button
                  type='button'
                  color='error'
                  onClick={() => {
                    setMfaCode('');
                    setIsMfaCodeModalOpen(false);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type='button'
                  color='success'
                  onClick={handleMfa}
                >
                  Confirmar
                </Button>
              </Grid>
            </Grid>
          </Loader>
        </Modal>
      )}
      {isPasswordUpdateOpen && (
        <PasswordUpdate
          isLoading={isPasswordUpdateLoading}
          isOpen={isPasswordUpdateOpen}
          setIsOpen={setIsPasswordUpdateOpen}
          onSuccess={values => {
            setIsPasswordUpdateLoading(true);

            dispatch(updateUserPassword(userId, values))
              .then(() => setIsPasswordUpdateOpen(false))
              .finally(() => setIsPasswordUpdateLoading(false));
          }}
        />
      )}
      <Card title="Preencha suas informações">
        <Formik
          enableReinitialize
          initialValues={{
            ...initialValues,
            isProfile: true,
            ...(isInitialRegistration) && {
              password: '',
              passwordConfirm: ''
            }
          }}
          validationSchema={validations}
          validateOnChange={false}
          validateOnMount={false}
          onSubmit={handleProfileSave}
        >
          {({ ...formikNewProps }) => {
            const {
              errors,
              handleChange,
              setFieldValue,
              values: {
                name,
                email,
                emailConfirm,
                phone,
                termsOfUse
              }
            } = formikNewProps;

            return(
              <Grid item xs={12}>
                <Form>
                  <Loader isLoading={isLoading}>
                    <Grid item xs={12}>
                      <Input
                        autoFocus={!isMobile}
                        name="name"
                        label="Seu nome"
                        value={name || formikProps.values.name}
                        error={errors.name || formikProps.errors.name}
                        helperText={errors.name || formikProps.errors.name}
                        onChange={e => {
                          handleChange(e);
                          formikProps.handleChange(e);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        noAutoComplete={isInitialRegistration}
                        name="phone"
                        type="phone"
                        label="Telefone"
                        placeholder="(00) 0000-0000"
                        value={phone || formikProps.values.phone}
                        error={errors.phone || formikProps.errors.phone}
                        helperText={errors.phone || formikProps.errors.phone}
                        onChange={e => {
                          handleChange(e);
                          formikProps.handleChange(e);
                        }}
                      />
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item md={isInitialRegistration ? 6 : 12} xs={12}>
                        <Input
                          noAutoComplete={isInitialRegistration}
                          required
                          name="email"
                          label="Email"
                          placeholder="usuario@exemplo.com"
                          value={email || formikProps.values.email}
                          error={errors.email || formikProps.errors.email}
                          helperText={errors.email || formikProps.errors.email}
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                      {isInitialRegistration && (
                        <Grid item md={6} xs={12}>
                          <Input
                            noAutoComplete
                            required
                            name="emailConfirm"
                            label="Confirmar email"
                            placeholder="usuario@exemplo.com"
                            value={emailConfirm || formikProps.values.emailConfirm}
                            error={errors.emailConfirm || formikProps.errors.emailConfirm}
                            helperText={errors.emailConfirm || formikProps.errors.emailConfirm}
                            onChange={e => {
                              handleChange(e);
                              formikProps.handleChange(e);
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                    {isInitialRegistration && (
                      <>
                        <Grid container spacing={2}>
                          <Grid item md={6} xs={12}>
                            <Input
                              name="password"
                              type="password"
                              label="Senha"
                              placeholder="Senha"
                              value={formikProps.values.password}
                              error={formikProps.errors.password}
                              helperText={formikProps.errors.password}
                              onChange={e => {
                                handleChange(e);
                                formikProps.handleChange(e);
                              }}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <Input
                              name="passwordConfirm"
                              type="password"
                              label="Confirmar senha"
                              placeholder="Confirmar senha"
                              value={formikProps.values.passwordConfirm}
                              error={formikProps.errors.passwordConfirm}
                              helperText={formikProps.errors.passwordConfirm}
                              onChange={e => {
                                handleChange(e);
                                formikProps.handleChange(e);
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Grid item md={6} xs={12} style={{ marginTop: 10, marginBottom: 10 }}>
                          {getPasswordStrength(formikProps.values.password || '')}
                          <Box style={{ marginTop: 10 }} />
                          {getPasswordRules(formikProps.values.password || '')}
                        </Grid>
                      </>
                    )}
                    {isInitialRegistration && (
                      <Grid container xs={12} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="primary">
                            <a
                              href={process.env.REACT_APP_TERMS_OF_USE_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Acesse os termos de uso
                            </a>
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="termsOfUse"
                                type="checkbox"
                                checked={termsOfUse || formikProps.values.termsOfUse}
                                onChange={e => handleCheckboxChange(e, setFieldValue)}
                                value={termsOfUse || formikProps.values.termsOfUse}
                              />
                            }
                            label={
                              <Typography style={{ fontSize: 12 }}>
                                Eu li e aceito todas as informações descritas nos
                                termos de uso
                              </Typography>
                            }
                          />
                        </Grid>
                        {formikProps.errors.termsOfUse && (
                          <FormHelperText error>
                            {formikProps.errors.termsOfUse}
                          </FormHelperText>
                        )}
                      </Grid>
                    )}
                  </Loader>
                  {!isInitialRegistration && (
                    <CardFooter>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                        {(!isUserAdmin && +mfa?.canDisableEmail2fa == 1) && (
                          <Button
                            type="button"
                            color="primary"
                            loading={isLoading}
                            onClick={() => setIsMfaModalOpen(true)}
                          >
                            {+mfa?.email2faEnabled == 1 ? 'Desativar Dupla Autenticação' : 'Ativar Dupla Autenticação'}
                          </Button>
                        )}
                        <Button
                          type="button"
                          color="primary"
                          loading={isLoading}
                          onClick={() => setIsPasswordUpdateOpen(true)}
                        >
                          Alterar Senha
                        </Button>
                        <Button
                          type="submit"
                          color="success"
                          loading={isLoading}
                        >
                          Salvar
                        </Button>
                      </div>
                    </CardFooter>
                  )}
                </Form>
              </Grid>
            );
          }}
        </Formik>
      </Card>
      {!isInitialRegistration && (
        <Card title="Relatórios" style={{ marginTop: 15 }}>
          <Grid container>
            <Grid item xs={12}>
              <Loader isLoading={isCashFlowLoading}>
                <Grid container>
                  <Grid item xs={12}>
                    {isMobile && (
                      <TableMobile
                        loading={isCashFlowLoading}
                        data={allEstablishmentsReportList?.map(item => {
                          return {
                            ...item,
                            descriptionColumn: {
                              index: 1,
                              label: 'Relatório',
                              value: <Typography color='textSecondary' variant='subtitle2'>{item?.description}</Typography>
                            },
                            dateTimeColumn: {
                              index: 2,
                              label: 'Data/Hora',
                              value: <Typography color='textSecondary' variant='subtitle2'>{moment(item?.createdAt).format('DD/MM/YYYY HH:mm:ss')}</Typography>
                            }
                          }
                        })}
                        actions={[
                          {
                            action: rowData => (
                              <Tooltip placement='top' title='Download' enterTouchDelay={0}>
                                <IconButton onClick={() => {
                                  const { reportId, description } = rowData || {};
                                  handleGenerateReport(reportId, description);
                                }}>
                                  <FontAwesomeIcon
                                    icon={faDownload}
                                    color="#022A5C"
                                    size="xs"
                                  />
                                </IconButton>
                              </Tooltip>
                            )
                          }
                        ]}
                      />
                    )}
                    {!isMobile && (
                      <MTable
                        loading={isCashFlowLoading}
                        headers={allEstablishmentsReportHeaders}
                        data={allEstablishmentsReportList?.filter(item => +item?.status == 2)?.map(item => {
                          return {
                            ...item,
                            createdAt: moment(item?.createdAt).format('DD/MM/YYYY HH:mm:ss')
                          }
                        })}
                        actions={[
                          rowData => ({
                            icon: () => (
                              <FontAwesomeIcon
                                icon={faDownload}
                                color="#022A5C"
                                size="xs"
                              />
                            ),
                            tooltip: 'Download',
                            onClick: (_, rowData) => {
                              const { reportId, description } = rowData || {};
                              handleGenerateReport(reportId, description);
                            }
                          })
                        ]}
                      />
                    )}
                  </Grid>
                </Grid>
              </Loader>
            </Grid>
          </Grid>
        </Card>
      )}
    </>
  );
}

Profile.defaultProps = {
  formikProps: {
    handleChange: () => {},
    setFieldValue: () => {},
    errors: {
      name: false,
      email: false,
      phone: false,
      password: false,
      passwordConfirm: false,
      termsOfUse: false
    },
    values: {
      name: '',
      email: '',
      phone: '',
      password: '',
      passwordConfirm: '',
      termsOfUse: false
    }
  }
};

Profile.propTypes = {
  formikProps: PropTypes.shape(),
  turnstileKey: PropTypes.number
};